import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib';
import { BusinessIdea } from '@orbit/shared/types';
import { getPrimaryBusinessIdea } from '@/lib/business-ideas';
import {
  getCachedBusinessIdea,
  setCachedBusinessIdea,
  invalidateBusinessIdeaCache,
} from '@/lib/cache';

interface UseBusinessIdeaReturn {
  businessIdea: BusinessIdea | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useBusinessIdea = (): UseBusinessIdeaReturn => {
  const { user } = useAuth();
  const [businessIdea, setBusinessIdea] = useState<BusinessIdea | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefetch, setLastRefetch] = useState(0);

  // Smart refetch logic - only refetch if enough time has passed
  const shouldRefetch = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRefetch = now - lastRefetch;
    const MIN_REFETCH_INTERVAL = 30 * 1000; // 30 seconds minimum between refetches

    return timeSinceLastRefetch > MIN_REFETCH_INTERVAL;
  }, [lastRefetch]);

  const fetchBusinessIdea = useCallback(
    async (force = false) => {
      if (!user) {
        setBusinessIdea(null);
        setLoading(false);
        return;
      }

      // Check if we should skip refetch
      if (!force && !shouldRefetch()) {
        return;
      }

      // Check cache first
      const cached = getCachedBusinessIdea(user.id);
      if (cached && !force) {
        setBusinessIdea(cached);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const userBusinessIdea = await getPrimaryBusinessIdea(user.id);

        if (userBusinessIdea) {
          // Cache the result
          setCachedBusinessIdea(user.id, userBusinessIdea);
          setBusinessIdea(userBusinessIdea);
        } else {
          // No business idea found
          setBusinessIdea(null);
        }

        // Update last refetch timestamp
        setLastRefetch(Date.now());
      } catch (err) {
        console.error('Error fetching business idea:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch business idea');
        setBusinessIdea(null);
      } finally {
        setLoading(false);
      }
    },
    [user, shouldRefetch],
  );

  useEffect(() => {
    fetchBusinessIdea();
  }, [fetchBusinessIdea]);

  const refetch = useCallback(() => {
    // Force a fresh fetch by clearing cache and bypassing refetch logic
    if (user) {
      invalidateBusinessIdeaCache(user.id);
    }
    fetchBusinessIdea(true);
  }, [fetchBusinessIdea, user]);

  return {
    businessIdea,
    loading,
    error,
    refetch,
  };
};
